import { BaseFieldProps } from '@epos/ui';
import { countries } from '@epos/utilities/constants';
import { Autocomplete, AutocompleteChangeReason, FilterOptionsState, TextField } from '@mui/material';
import clsx from 'clsx';
import { HTMLAttributes, SyntheticEvent, useCallback } from 'react';

import 'flag-icons/css/flag-icons.min.css';

type Country = (typeof countries)[0];

const PhoneCodeField: React.FC<BaseFieldProps> = ({
  onChange = () => {},
  value,
  slotProps,
  readOnly,
  disabled,
  ...props
}) => {
  const selectOption = countries.find(({ dial_code: optionValue }) => optionValue === value);
  const renderOption = useCallback(
    (optionProps: HTMLAttributes<HTMLLIElement>, option: Country) => (
      <li
        {...optionProps}
        className={clsx(optionProps.className, '!flex min-w-[160px] !items-center !justify-between')}
      >
        <div>
          <span className={`fi fi-${option?.code?.toLowerCase()} mr-1`} /> {option.name}
        </div>
        <div>{option.dial_code}</div>
      </li>
    ),
    []
  );

  const getOptionLabel = useCallback((option: (typeof countries)[0]) => `${option.flag} ${option.dial_code}`, []);

  const handleChange = useCallback((_: SyntheticEvent, newValue: Country | null, reason: AutocompleteChangeReason) => {
    if (reason === 'selectOption') {
      onChange(newValue?.dial_code);
    } else if (reason === 'clear') {
      onChange('');
    }
  }, []);

  const filterOptions = useCallback((options: Array<Country>, state: FilterOptionsState<Country>) => {
    return options.filter(
      (option) =>
        option.dial_code.includes(state.inputValue) ||
        option.name.toLowerCase().includes(state.inputValue?.toLowerCase()) ||
        option.code.toLowerCase().includes(state.inputValue?.toLowerCase())
    );
  }, []);

  return (
    <Autocomplete
      value={selectOption}
      disableClearable
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      options={countries}
      onChange={handleChange}
      filterOptions={filterOptions}
      readOnly={readOnly}
      disabled={disabled}
      slotProps={{
        paper: {
          className: '!w-max'
        }
      }}
      renderInput={(params) => (
        <TextField {...params} {...props} size='medium' InputLabelProps={{ shrink: true }} className='!w-28' />
      )}
    />
  );
};

export default PhoneCodeField;
